/**
 * ChecklistDetailSections — Tab-based layout for the checklist detail sheet
 *
 * 4 tabs: Questions & Findings, Evidence, AI Insights, Activity.
 * Active tab is controlled by parent so it persists across item navigation.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type {
  UIChecklistItem,
  GeneratedQuestion,
  ChecklistItemAnswer,
  AuditId,
  DocumentId,
} from "@/packages/types";
import { EvidenceContent } from "./ChecklistEvidenceSection";
import { ActivityContent } from "./ChecklistActivitySection";
import {
  AISummarySection,
  AIInformationContent,
  AIExamplesContent,
} from "./ChecklistAIInfoSection";
import { QuestionsAndFindingsSection } from "./QuestionsAndFindingsSection";

// ─── Tab Types ───────────────────────────────────────────────────────────────

export const TABS = ["questions", "evidence", "ai", "activity"] as const;
export type TabId = (typeof TABS)[number];

// ─── Questions Badge Logic ───────────────────────────────────────────────────

function getQuestionsBadge(
  questions: GeneratedQuestion[],
  answers: ChecklistItemAnswer[],
) {
  if (!questions.length) return null;
  const required = questions.filter((q) => q.required);
  const answeredOrders = new Set(answers.map((a) => a.question.order));
  const requiredAnswered = required.filter((q) => answeredOrders.has(q.order));
  const allRequiredAnswered = requiredAnswered.length === required.length;
  const noneAnswered = answeredOrders.size === 0;
  const dotColor = allRequiredAnswered
    ? "green"
    : noneAnswered && required.length > 0
      ? "red"
      : "amber";
  return { label: `${answeredOrders.size}/${questions.length}`, dotColor };
}

const DOT_COLOR_CLASSES: Record<string, string> = {
  green: "bg-green-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

// ─── Tab Bar ─────────────────────────────────────────────────────────────────

interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  evidenceCount: number;
  questions: GeneratedQuestion[];
  answers: ChecklistItemAnswer[];
  findingsCount: number;
  findingsGenerating: boolean;
}

function TabBar({
  activeTab,
  onTabChange,
  evidenceCount,
  questions,
  answers,
  findingsCount,
  findingsGenerating,
}: TabBarProps) {
  const { t } = useTranslation("audits");
  const questionsBadge = getQuestionsBadge(questions, answers);
  const hasFindingsAlert = findingsCount > 0;

  const getTabLabel = (tabId: TabId): string => {
    const labels: Record<TabId, string> = {
      evidence: t("checklist.tabs.evidence"),
      questions: t("checklist.tabs.questions"),
      ai: t("checklist.tabs.ai"),
      activity: t("checklist.tabs.activity"),
    };
    return labels[tabId];
  };

  return (
    <div className="flex items-center gap-0 border-b border-border">
      {TABS.map((id) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onTabChange(id)}
            className={`relative px-3 py-2.5 text-[12px] font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
              isActive ? "text-primary" : "text-muted hover:text-secondary"
            }`}
          >
            {getTabLabel(id)}

            {/* Evidence badge */}
            {id === "evidence" && evidenceCount > 0 && (
              <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                {evidenceCount}
              </span>
            )}

            {/* Questions & Findings badge */}
            {id === "questions" && (
              <span className="inline-flex items-center gap-1">
                {questionsBadge && (
                  <>
                    <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                      {questionsBadge.label}
                    </span>
                    <span
                      className={`size-1.5 rounded-full ${DOT_COLOR_CLASSES[questionsBadge.dotColor]}`}
                    />
                  </>
                )}
                {hasFindingsAlert && (
                  <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-red-500/15 px-1 text-[9px] font-semibold text-red-600">
                    {findingsCount}
                  </span>
                )}
                {findingsGenerating && (
                  <span className="size-1.5 rounded-full shrink-0 bg-accent animate-pulse" />
                )}
              </span>
            )}

            {isActive && (
              <motion.div
                layoutId="checklist-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                transition={springs.snappy}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

type AISubTab = "summary" | "information" | "examples";

function AIInsightsTab({
  item,
  onGenerateSummary,
  onGenerateEverything,
  onGenerateInformation,
  onGenerateExamples,
}: {
  item: UIChecklistItem;
  onGenerateSummary?: () => void;
  onGenerateEverything?: () => void;
  onGenerateInformation?: (instructions: string) => void;
  onGenerateExamples?: (instructions: string) => void;
}) {
  const { t } = useTranslation("audits");
  const [aiSubTab, setAiSubTab] = useState<AISubTab>("summary");

  const hasInformation = !!item.informationReport;
  const examplesCount = item.exampleDocuments?.length ?? 0;
  const infoPending = item.informationStatus === "pending";
  const examplesPending = item.exampleDocumentsStatus === "pending";

  const getAISubTabLabel = (tabId: AISubTab): string => {
    const labels: Record<AISubTab, string> = {
      summary: t("checklist.aiSubTabs.summary"),
      information: t("checklist.aiSubTabs.information"),
      examples: t("checklist.aiSubTabs.examples"),
    };
    return labels[tabId];
  };

  return (
    <div className="pt-2">
      {/* Sub-tab strip */}
      <div className="flex items-center gap-0 border-b border-border mb-4">
        {(["summary", "information", "examples"] as AISubTab[]).map((id) => {
          const isActive = aiSubTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setAiSubTab(id)}
              className={`relative px-3 py-2.5 text-[12px] font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
                isActive ? "text-primary" : "text-muted hover:text-secondary"
              }`}
            >
              {getAISubTabLabel(id)}

              {/* Information badge */}
              {id === "information" && (hasInformation || infoPending) && (
                <span
                  className={`size-1.5 rounded-full shrink-0 ${infoPending ? "bg-accent animate-pulse" : "bg-green-500"}`}
                />
              )}

              {/* Examples badge */}
              {id === "examples" && examplesCount > 0 && (
                <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                  {examplesCount}
                </span>
              )}
              {id === "examples" && examplesPending && examplesCount === 0 && (
                <span className="size-1.5 rounded-full shrink-0 bg-accent animate-pulse" />
              )}

              {isActive && (
                <motion.div
                  layoutId="ai-sub-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                  transition={springs.snappy}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        {aiSubTab === "summary" && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AISummarySection
              item={item}
              onGenerateSummary={onGenerateSummary}
              onGenerateEverything={onGenerateEverything}
            />
          </motion.div>
        )}
        {aiSubTab === "information" && (
          <motion.div
            key="information"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AIInformationContent
              item={item}
              onGenerateInformation={onGenerateInformation}
            />
          </motion.div>
        )}
        {aiSubTab === "examples" && (
          <motion.div
            key="examples"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AIExamplesContent
              item={item}
              onGenerateExamples={onGenerateExamples}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

interface ChecklistDetailSectionsProps {
  item: UIChecklistItem;
  auditId: AuditId;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onGenerateSummary?: () => void;
  onGenerateEverything?: () => void;
  onGenerateInformation?: (instructions: string) => void;
  onGenerateExamples?: (instructions: string) => void;
  onFindRelatedDocuments?: () => void;
  onOpenDocument?: (docId: DocumentId) => void;
  findingsCount: number;
}

export function ChecklistDetailSections({
  item,
  auditId,
  activeTab,
  onTabChange,
  onGenerateSummary,
  onGenerateEverything,
  onGenerateInformation,
  onGenerateExamples,
  onOpenDocument,
  findingsCount,
}: ChecklistDetailSectionsProps) {
  const evidenceCount = item.linkedDocumentIds?.length ?? 0;
  const questions = item.questions ?? [];
  const answers = item.answers ?? [];
  const findingsGenerating = item.findingsGenerationStatus === "generating";

  const tabContent: Record<TabId, ReactNode> = {
    evidence: (
      <div className="pt-4">
        <EvidenceContent
          item={item}
          auditId={auditId}
          onOpenDocument={onOpenDocument}
        />
      </div>
    ),
    ai: (
      <AIInsightsTab
        item={item}
        onGenerateSummary={onGenerateSummary}
        onGenerateEverything={onGenerateEverything}
        onGenerateInformation={onGenerateInformation}
        onGenerateExamples={onGenerateExamples}
      />
    ),
    questions: (
      <div className="pt-4">
        <QuestionsAndFindingsSection
          item={item}
          checklistItemId={item.id}
          onSwitchToEvidence={() => onTabChange("evidence")}
          onGenerateEverything={onGenerateEverything}
          onOpenSummaryTab={() => onTabChange("ai")}
        />
      </div>
    ),
    activity: (
      <div className="pt-4">
        <ActivityContent checklistItemId={item.id} />
      </div>
    ),
  };

  return (
    <div>
      <TabBar
        activeTab={activeTab}
        onTabChange={onTabChange}
        evidenceCount={evidenceCount}
        questions={questions}
        answers={answers}
        findingsCount={findingsCount}
        findingsGenerating={findingsGenerating}
      />
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
        >
          {tabContent[activeTab]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
